// Kept free of React and the API client: the Worker in worker/ renders the
// same tags into the static HTML, so a link preview and the hydrated page say
// the same thing.
import { primaryImage, type ImageRef } from "./primary-image";

/** The fields of an ApiProduct a product's tags are made from. */
export interface SeoProduct {
  slug: string;
  name: string;
  tagline?: string;
  /** Markdown, as authored in the admin. */
  description?: string;
  line?: string;
  soldOut?: boolean;
  images?: (ImageRef & { mediaType?: string })[];
  skus?: SeoSku[];
}

/** The fields of an ApiSku the structured data reads. */
export interface SeoSku {
  code: string;
  priceIdr: number;
  isActive?: boolean;
  soldOut?: boolean;
  priceInvalid?: boolean;
}

/** Google shows ~155 characters of a description before it truncates. */
const DESCRIPTION_MAX = 155;

/** The size the Worker renders a product's preview image at: the 1.91:1 every platform crops to. */
export const PRODUCT_OG_WIDTH = 1200;
export const PRODUCT_OG_HEIGHT = 630;

export interface ProductSeo {
  title: string;
  description: string;
  path: string;
  /** Site-relative. Undefined when the product has no photo, for the shared OG image. */
  image?: string;
  imageAlt: string;
}

/**
 * What a product page says about itself to search engines and link previews.
 *
 * The image is the Worker's /og/produk/:slug.jpg, not the upload itself: the
 * uploads are transparent PNG cutouts around 400 KB, which WhatsApp drops
 * (too large) and Facebook can paint on black. The Worker re-encodes the
 * thumbnail as a 1200×630 JPEG on white. The image id rides along as ?v= so a
 * new thumbnail is a new URL, and no platform's cache serves the old one.
 */
export function productSeo(product: SeoProduct): ProductSeo {
  const image = primaryImage(product);
  return {
    title: `${product.name} — Kanata Indonesia`,
    description: describe(product),
    path: `/produk/${product.slug}`,
    image: image ? `/og/produk/${encodeURIComponent(product.slug)}.jpg?v=${encodeURIComponent(image.id)}` : undefined,
    imageAlt: image?.alt || product.name,
  };
}

/**
 * The search-result description: the tagline, then as much of the authored
 * description as fits, cut at a word. The tagline alone was ~40 characters,
 * which is the snippet Google then fills from whatever text it finds first on
 * the page — usually the navigation.
 */
function describe(product: SeoProduct): string {
  const parts = [product.tagline, plainText(product.description ?? "")]
    .map((p) => p?.trim())
    .filter(Boolean) as string[];
  if (parts.length === 0) return product.name;
  const text = parts.map((p) => (/[.!?]$/.test(p) ? p : p + ".")).join(" ");
  if (text.length <= DESCRIPTION_MAX) return text;
  const cut = text.slice(0, DESCRIPTION_MAX - 1);
  return cut.slice(0, cut.lastIndexOf(" ")).replace(/[\s,.;:—-]+$/, "") + "…";
}

/**
 * Markdown down to its sentences: headings dropped whole (they are labels, and
 * "Kenapa Karya Karya Klasik adalah…" reads as a typo in a search result),
 * then emphasis, list marks and link targets stripped.
 */
export function plainText(markdown: string): string {
  return markdown
    .replace(/^\s{0,3}#{1,6}\s.*$/gm, "")
    .replace(/!\[[^\]]*\]\([^)]*\)/g, "")
    .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/^\s{0,3}([-*+]|\d+\.)\s+/gm, "")
    .replace(/[*_`>~]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

/** Live, priced, sellable variants: what an offer may quote. */
function offerSkus(product: SeoProduct): SeoSku[] {
  return (product.skus ?? []).filter((s) => s.isActive !== false && !s.priceInvalid && s.priceIdr > 0);
}

/**
 * schema.org Product + BreadcrumbList for a product page, the structured data
 * behind Google's price and availability in results. One AggregateOffer over
 * the variants rather than an Offer each: a desk has two dozen size and colour
 * combinations, and the range is what a searcher compares.
 *
 * Rendered in the static HTML by the Worker and again by the page on hydrate,
 * from this one function, so the two cannot disagree.
 */
export function productJsonLd(product: SeoProduct, siteUrl: string, brandName: string): object[] {
  const url = `${siteUrl}/produk/${product.slug}`;
  const images = (product.images ?? [])
    .filter((i) => (i.mediaType ?? "image") === "image")
    .slice(0, 6)
    .map((i) => i.publicUrl);
  const skus = offerSkus(product);
  const prices = skus.map((s) => s.priceIdr);
  const inStock = !product.soldOut && skus.some((s) => !s.soldOut);

  const productLd: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    description: describe(product),
    url,
    brand: { "@type": "Brand", name: brandName },
  };
  if (images.length) productLd.image = images;
  if (prices.length) {
    productLd.offers = {
      "@type": "AggregateOffer",
      priceCurrency: "IDR",
      lowPrice: Math.min(...prices),
      highPrice: Math.max(...prices),
      offerCount: prices.length,
      availability: inStock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
      url,
    };
  }

  const breadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Beranda", item: `${siteUrl}/` },
      { "@type": "ListItem", position: 2, name: "Katalog", item: `${siteUrl}/katalog` },
      { "@type": "ListItem", position: 3, name: product.name, item: url },
    ],
  };
  return [productLd, breadcrumb];
}

/**
 * JSON for inside a <script> element. "<" is escaped so an authored
 * description containing "</script>" cannot close the element early.
 */
export function jsonLdText(data: unknown): string {
  return JSON.stringify(data).replace(/</g, "\\u003c");
}
